import { createEffect, createStore } from 'effector'
import type { ApiResponseError } from '../../interfaces/errors'
import { stateful } from '../../lib/stateful'
import type {
  TvShow,
  TvShowEpisode,
  TvShowSeason,
  TvShowsGenre,
  TvShowsGenresToLanguages,
  TvShowsLanguage,
  TvShowsPlaylist,
} from './index.h'

export type {
  TvShow,
  TvShowEpisode,
  TvShowSeason,
  TvShowsGenre,
  TvShowsGenresToLanguages,
  TvShowsLanguage,
  TvShowsPlaylist,
}

type GetOneParams = { id: number }
type SortParams = { by?: string; order?: string }
export type PlaybackUrl = { url: string }

type RequestHeaders = { headers: Headers }

type FavoriteParams = { id: number }

type PageParams = { limit?: number; offset?: number }
type LanguageParam = { languageId?: number }
type SeasonParams = { seasonId: number }
type EpisodeParams = { episodeId: number }
type WithEpisodeCountsParams = { withEpisodeCounts?: boolean }
type GetByGenreId = { genreId?: number }
type GetByLanguageId = { languageId?: number }
type GetByLanguageQueryParamId = { languageIdQueryParam?: number }
type GetManyTvShowsParams = GetByGenreId &
  GetByLanguageId &
  PageParams &
  GetByLanguageQueryParamId
type WithDescriptions = { withDescriptions?: 0 | 1 }

export interface TvShowsGenres extends Array<TvShowsGenre> {
  total: number
}

export interface TvShowsLanguages extends Array<TvShowsLanguage> {
  total: number
}

export interface TvShowEpisodes extends Array<TvShowEpisode> {
  total: number
}

export interface TvShowList extends Array<TvShow> {
  total: number
}

// *
// * base API effects
// *
export const base = {
  getTvShowsGenresFx: createEffect<WithDescriptions, TvShowsGenres>(),
  getTvShowsLanguagesFx: createEffect<WithDescriptions, TvShowsLanguages>(),
  getTvShowsGenresToLanguagesFx: createEffect<void, TvShowsGenresToLanguages>(),
  getTvShowsByGenreFx: createEffect<
    GetOneParams & PageParams & LanguageParam,
    TvShowList
  >(),
  getTvShowFx: createEffect<GetOneParams, TvShow>(),
  getTvShowSeasonsByIdFx: createEffect<
    GetOneParams & WithEpisodeCountsParams,
    TvShowSeason[]
  >(),
  getTvShowEpisodesBySeasonIdFx: createEffect<
    PageParams & SeasonParams & GetOneParams,
    TvShowEpisodes
  >(),
  getOneTvShowEpisodeUrlPlaybackFx: createEffect<
    GetOneParams & SeasonParams & EpisodeParams & RequestHeaders,
    PlaybackUrl
  >(),
  getTvShowsByCollectionFx: createEffect<
    GetOneParams & PageParams & SortParams,
    TvShowList
  >(),
  getTvShowsByHashtagFx: createEffect<GetOneParams & PageParams, TvShowList>(),
  getFavoritesFx: createEffect<PageParams, TvShowList>(),
  addToFavoritesFx: createEffect<FavoriteParams, void>(),
  removeFromFavoritesFx: createEffect<FavoriteParams, void>(),
  getSeasonsWithEpisodesFx: createEffect<GetOneParams, TvShowsPlaylist>(),
  getRecommendedFx: createEffect<PageParams, TvShowList>(),
  getTvShowsFx: createEffect<PageParams & SortParams, TvShowList>(),
  getTvShowsByFx: createEffect<GetManyTvShowsParams, TvShowList>(),
}

export const pageableEpisodes = (
  initial?: PageParams & SeasonParams & GetOneParams
) =>
  stateful({
    effect: base.getTvShowEpisodesBySeasonIdFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
    },
  })

export const pageableByCollection = (
  initial?: GetOneParams & PageParams & SortParams
) =>
  stateful({
    effect: base.getTvShowsByCollectionFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
      sort: ({ by, order }: SortParams, params) => ({ ...params, by, order }),
      by: (by: string | undefined, params) => ({ ...params, by }),
      order: (order: string | undefined, params) => ({ ...params, order }),
    },
  })

export const pageableByHashtag = (initial?: GetOneParams & PageParams) =>
  stateful({
    effect: base.getTvShowsByHashtagFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
    },
  })

export const pageableFavorites = (initial?: PageParams) =>
  stateful({
    effect: base.getFavoritesFx,
    initial,
    methods: {
      limit: (n: number) => ({ limit: n }),
      offset: (n: number) => ({ offset: n }),
      next: (_: void) => ({}),
      prev: (_: void, { offset = 0, limit = 36 }) => ({
        offset: Math.max(offset - limit, 0),
      }),
    },
  })

export const pageableBy = (initial?: GetManyTvShowsParams) =>
  stateful({
    effect: base.getTvShowsByFx,
    initial,
    methods: {
      limit: (n: number) => ({ limit: n }),
      offset: (n: number) => ({ offset: n }),
      next: (_: void, { offset = 0, limit = 36 }) => ({
        offset: offset + limit,
      }),
      prev: (_: void, { offset = 0, limit = 36 }) => ({
        offset: Math.max(offset - limit, 0),
      }),
    },
  })

export const error = {
  getTvShowFx: createStore<null | ApiResponseError>(null).on(
    base.getTvShowFx.fail,
    (_, { error }) => {
      // @ts-ignore because error does not exist in type Error
      const response = error?.error?.response
      if (!response) return

      return {
        code: response.status,
        message: response.statusText,
      }
    }
  ),
}
